import { Heading, Stack, Text } from "@chakra-ui/react";
import * as React from "react";
import { FC } from "react";

import Card from "components/Chakra/Card";
import Container from "components/Chakra/Container";
import CustomLink, { ILinkProps } from "components/Chakra/CustomLink";
import MetaHead from "components/MetaHead";
import { createSearchUrl } from "scenes/Search/createSearchUrl";
import { TFilter } from "scenes/Search/types";
import { generateUniqueKey } from "utils/reactHelpers";
import {
  propertyRentCities,
  flatRentCities,
  propertySaleCities,
  flatSaleCities,
  houseRentCities,
  houseSaleCities,
  prague,
  studentCities,
} from "./data";
import SearchLink from "components/Chakra/SearchLink";

type TOfferType = TFilter["offerType"];

interface ILine {
  linkProps?: ILinkProps;
  text: string;
  external?: string;
  search?: {
    location?: string;
    offerType?: TOfferType;
    noCommission?: boolean;
    propertyType?: TFilter["propertyType"];
  };
}

const Line: FC<ILine> = ({ linkProps, text, external, search }) => {
  const getSearchUrl = () =>
    createSearchUrl({
      filter: {
        offerType: search?.offerType ?? "rent",
        propertyType: search?.propertyType,
        isNoCommission: search?.noCommission,
      },
      location: search?.location,
    });

  if (search) {
    return (
      <SearchLink href={getSearchUrl()} key={generateUniqueKey()}>
        <Text w="205px" textColor="primary">
          {text}
        </Text>
      </SearchLink>
    );
  }

  return (
    <CustomLink
      {...linkProps}
      href={external}
      key={generateUniqueKey()}
      target={external?.length ? "_blank" : "_self"}
    >
      <Text w="205px" textColor="primary">
        {text}
      </Text>
    </CustomLink>
  );
};

interface IProps {
  lines: Array<ILine>;
}

const List: FC<IProps> = ({ lines }) => (
  <Stack>
    {lines.map((line, i) => (
      <Line
        linkProps={line.linkProps}
        text={line.text}
        search={line.search}
        key={i}
        external={line.external}
      />
    ))}
  </Stack>
);

const PageMap: FC = () => (
  <>
    <MetaHead
      title="Mapa stránek"
      description="Pronájem bytu v největších českých městech, spolubydlení v největších studentských městech a pronájem bytu v nejžádanějších pražských čtvrtí."
      canonicalUrl="https://www.ulovdomov.cz/mapa-stranek"
    />
    <Container>
      <Heading as="h1" my="5rem">
        Mapa stránek UlovDomov.cz
      </Heading>
      <Heading as="h2" mb="3rem" textAlign="center" fontWeight="light">
        Služby a produkty
      </Heading>
      <Card
        styleProps={{
          mb: "60px",
          p: "40px 36px 40px 50px",
        }}
      >
        <Stack
          direction="row"
          wrap="wrap"
          gap="40px"
          align={{ base: "center", md: "flex-start" }}
          justify={{ base: "center", md: "center" }}
          spacing={0}
        >
          <Stack maxW="200px">
            <Heading as="h3" mb="1rem" textAlign="left">
              Pro zájemce
            </Heading>
            <List
              lines={[
                {
                  text: "Vyhledávání bydlení",
                  search: { offerType: "rent" },
                },
                {
                  text: "Hlídací pes",
                  linkProps: { to: "/hlidaci-pes" },
                },
                {
                  text: "Sebeprověření",
                  linkProps: { to: "/sebeprovereni" },
                },
                {
                  text: "Realitní blog",
                  linkProps: { to: "/radce/najemnik" },
                },
                {
                  text: "Tahák na prohlídku",
                  external: "/pruvodce-najemnika",
                },
              ]}
            />
          </Stack>
          <Stack maxW="200px">
            <Heading as="h3" mb="1rem" textAlign="left">
              Pro realitní makléře
            </Heading>
            <List
              lines={[
                {
                  text: "Inzerce nemovitosti",
                  linkProps: { to: "/vlozeni-inzeratu/formular/kontakt" },
                },
                {
                  text: "Velkoobjemová inzerce",
                  linkProps: { to: "/pro-realitni-kancelare" },
                },
                {
                  text: "Prověření rizikovosti osob",
                  linkProps: { to: "/provereni-rizikovosti-osob" },
                },
                {
                  text: "Balíčky prověření",
                  linkProps: { to: "/balicky-provereni" },
                },
                {
                  text: "Vizuální prezentace",
                  linkProps: { to: "/vizualni-prezentace" },
                },
                {
                  text: "Realitní blog",
                  linkProps: { to: "/radce/majitel" },
                },
              ]}
            />
          </Stack>
          <Stack maxW="200px">
            <Heading as="h3" mb="1rem" textAlign="left">
              Pro soukromé majitele
            </Heading>
            <List
              lines={[
                {
                  text: "Soukromá inzerce",
                  linkProps: { to: "/vyhody/soukroma-inzerce" },
                },
                {
                  text: "Prověření rizikovosti osob",
                  linkProps: { to: "/provereni-rizikovosti-osob" },
                },
                {
                  text: "Služba Ideální nájemce",
                  external:
                    "https://idealninajemce.cz/?utm_source=ulovdomov&utm_medium=referal&utm_campaign=sluzba-idealni-najemce",
                },
                {
                  text: "Vzory smluv",
                  external:
                    "https://idealninajemce.cz/pronajimam/vzory-smluv/?utm_source=ulovdomov&utm_medium=referal&utm_campaign=sluzba-idealni-najemce",
                },
                {
                  text: "Prodej nemovitosti",
                  linkProps: { to: "/prodeje-nemovitosti" },
                },
                {
                  text: "Pronájem bez starosti",
                  external:
                    "https://pronajem.idealninajemce.cz/?utm_source=ulovdomov&utm_medium=referal&utm_campaign=pronajem-s-garanci",
                },
                {
                  text: "Vizuální prezentace",
                  linkProps: { to: "/vizualni-prezentace" },
                },
                {
                  text: "Nacenění nemovitosti",
                  external:
                    "https://idealninajemce.cz/pronajimam/naceneni-najmu/?utm_source=ulovdomov&utm_medium=referal&utm_campaign=sluzba-idealni-najemce",
                },
                {
                  text: "Cenové mapy",
                  external:
                    "https://idealninajemce.cz/cenova-mapa-najemneho/?utm_source=ulovdomov&utm_medium=referal&utm_campaign=sluzba-idealni-najemce",
                },
                {
                  text: "Realitní blog",
                  linkProps: { to: "/radce/majitel" },
                },
              ]}
            />
          </Stack>
        </Stack>
      </Card>
      <Heading
        as="h2"
        mb="3rem"
        mt="5rem"
        textAlign="center"
        fontWeight="light"
      >
        Pronájem nemovitosti v největších českých městech
      </Heading>
      <Stack flexWrap="wrap" direction="row" spacing={0}>
        {propertyRentCities.map((city, i) => (
          <Line key={i} text={city} search={{ location: city }} />
        ))}
      </Stack>
      <Heading
        as="h2"
        mb="3rem"
        mt="5rem"
        textAlign="center"
        fontWeight="light"
      >
        Pronájem bytu v největších českých městech
      </Heading>
      <Stack flexWrap="wrap" direction="row" spacing={0}>
        {flatRentCities.map((city, i) => (
          <Line
            key={i}
            text={city}
            search={{ location: city, propertyType: "flat" }}
          />
        ))}
      </Stack>
      <Heading
        as="h2"
        mb="3rem"
        mt="5rem"
        textAlign="center"
        fontWeight="light"
      >
        Pronájem domu v největších českých městech
      </Heading>
      <Stack flexWrap="wrap" direction="row" spacing={0}>
        {houseRentCities.map((city, i) => (
          <Line
            key={i}
            text={city}
            search={{ location: city, propertyType: "house" }}
          />
        ))}
      </Stack>
      <Heading
        as="h2"
        mb="3rem"
        mt="5rem"
        textAlign="center"
        fontWeight="light"
      >
        Prodej nemovitosti v největších českých městech
      </Heading>
      <Stack flexWrap="wrap" direction="row" spacing={0}>
        {propertySaleCities.map((city, i) => (
          <Line
            key={i}
            text={city}
            search={{ location: city, offerType: "sale" }}
          />
        ))}
      </Stack>
      <Heading
        as="h2"
        mb="3rem"
        mt="5rem"
        textAlign="center"
        fontWeight="light"
      >
        Prodej bytu v největších českých městech
      </Heading>
      <Stack flexWrap="wrap" direction="row" spacing={0}>
        {flatSaleCities.map((city, i) => (
          <Line
            key={i}
            text={city}
            search={{ location: city, propertyType: "flat", offerType: "sale" }}
          />
        ))}
      </Stack>
      <Heading
        as="h2"
        mb="3rem"
        mt="5rem"
        textAlign="center"
        fontWeight="light"
      >
        Prodej domu v největších českých městech
      </Heading>
      <Stack flexWrap="wrap" direction="row" spacing={0}>
        {houseSaleCities.map((city, i) => (
          <Line
            key={i}
            text={city}
            search={{
              location: city,
              propertyType: "house",
              offerType: "sale",
            }}
          />
        ))}
      </Stack>
      <Heading
        as="h2"
        mb="3rem"
        mt="5rem"
        textAlign="center"
        fontWeight="light"
      >
        Pronájem bytu bez provize v největších českých městech
      </Heading>
      <Stack flexWrap="wrap" direction="row" spacing={0}>
        {flatRentCities.map((city, i) => (
          <Line
            key={i}
            text={city}
            search={{
              location: city,
              propertyType: "flat",
              offerType: "rent",
              noCommission: true,
            }}
          />
        ))}
      </Stack>
      <Heading
        as="h2"
        mb="3rem"
        mt="5rem"
        textAlign="center"
        fontWeight="light"
      >
        Spolubydlení v největších studentských městech
      </Heading>
      <Stack flexWrap="wrap" direction="row" spacing={0}>
        {studentCities.map((city, i) => (
          <Line
            key={i}
            text={city}
            search={{
              location: city,
              propertyType: "flat",
              offerType: "coliving",
            }}
          />
        ))}
      </Stack>
      <Heading
        as="h2"
        mb="3rem"
        mt="5rem"
        textAlign="center"
        fontWeight="light"
      >
        Pronájem bytu v nejžádanějších pražských čtvrtích
      </Heading>
      <Stack flexWrap="wrap" direction="row" mb="180px" spacing={0}>
        {prague.map((city, i) => (
          <Line
            key={i}
            text={city}
            search={{ location: city, propertyType: "flat", offerType: "rent" }}
          />
        ))}
      </Stack>
    </Container>
  </>
);

export default PageMap;
